import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { formatDateInBusinessTZ } from '../../utils/date';
import { Modal } from '../../components/Modal';
import { getDeviceType, DeviceIcon } from '../../components/DeviceIcon';
import { Copy, Edit } from 'lucide-react';
import { CardPaymentBadge, BankPaymentBadge } from '../../components/PaymentMethodBadge';
import { formatPhoneCanonical, handlePhoneInput } from '../../utils/phone';
import {
  ADDRESS_MAX_LENGTH,
  POSTAL_CODE_ERROR,
  POSTAL_CODE_INPUT_MAX_LENGTH,
  changedAddressFields,
  normalizeAddress,
  parsePostalCode,
} from '../../utils/address';
import { analytics } from '../../utils/analytics';
import { ParentAccountDetails } from '../components/ParentAccountDetails';
import { FosterCityResidentBadge } from '../components/FosterCityResidentBadge';
import { isFosterCityZip } from '../../utils/eligibilityDiscounts';
import { PARENT_PARAM, useFocusedRecordId } from '../utils/recordLinks';
import { FocusedRecordBanner } from '../components/FocusedRecordBanner';

interface CardOnFile {
  brand: string | null;
  last4: string | null;
  is_default: boolean;
}

interface BankOnFile {
  bank_name: string | null;
  last4: string | null;
  account_type: string | null;
  is_default: boolean;
}

interface Parent {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  // Optional; the parent may have entered them at sign-up or on their Account page. Read with
  // `*`, so undefined rather than null if the columns are missing.
  address?: string | null;
  postal_code?: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  // Payment preferences (optional; populated via separate query)
  default_payment_method?: 'card' | 'bank_account' | 'zelle' | null;
  autopay_enabled?: boolean;
  // All payment methods on file
  cards_on_file?: CardOnFile[];
  banks_on_file?: BankOnFile[];
  student_parents: {
    student: {
      first_name: string;
      last_name: string;
      id: string;
    };
    relationship: string;
    is_primary: boolean;
  }[];
  latest_event?: {
    event_type: string;
    created_at: string;
    user_agent: string | null;
    metadata: Record<string, unknown>;
  };
}

interface StudentSearchResult {
  id: string;
  first_name: string;
  last_name: string;
}



interface EditParentModalProps {
  isOpen: boolean;
  onClose: () => void;
  parent: Parent | null;
  onSave: (parentData: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string | null;
    postal_code: string | null;
    relationshipsToRemove: string[];
    relationshipsToAdd: Array<{
      student_id: string;
      relationship: string;
      is_primary: boolean;
    }>;
  }) => Promise<void>;
  onDelete: (parentId: string) => Promise<void>;
  isLoading: boolean;
  isDeleting: boolean;
}

function EditParentModal({ isOpen, onClose, parent, onSave, onDelete, isLoading, isDeleting }: EditParentModalProps) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [currentRelationships, setCurrentRelationships] = useState<Parent['student_parents']>([]);
  const [relationshipsToRemove, setRelationshipsToRemove] = useState<string[]>([]);
  const [relationshipsToAdd, setRelationshipsToAdd] = useState<Array<{
    student_id: string;
    student_info: StudentSearchResult;
    relationship: string;
    is_primary: boolean;
  }>>([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentSearchResults, setStudentSearchResults] = useState<StudentSearchResult[]>([]);
  const [searchingStudents, setSearchingStudents] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [hasBalanceTransactions, setHasBalanceTransactions] = useState(false);

  // Update form data when parent prop changes
  useEffect(() => {
    if (parent) {
      setFormData({
        first_name: parent.first_name || '',
        last_name: parent.last_name || '',
        email: parent.email || '',
        phone: formatPhoneCanonical(parent.phone || ''),
        address: parent.address || '',
        postal_code: parent.postal_code || '',
      });
      setCurrentRelationships(parent.student_parents || []);
      setRelationshipsToRemove([]);
      setRelationshipsToAdd([]);
      setStudentSearch('');
      setStudentSearchResults([]);
      setShowAddStudent(false);
      setFormError(null);

      // Check whether this parent has any wallet-related data (hides Delete button if so).
      // Covers: balance_transactions (via account), zelle intents, ACH pending purchases.
      const parentId = parent.id;
      Promise.all([
        supabase
          .from('balance_accounts')
          .select('id')
          .eq('parent_id', parentId)
          .maybeSingle()
          .then(({ data: account }) => {
            if (!account) return 0;
            return supabase
              .from('balance_transactions')
              .select('id', { count: 'exact', head: true })
              .eq('account_id', account.id)
              .then(({ count }) => count ?? 0);
          }),
        supabase
          .from('balance_zelle_intents')
          .select('id', { count: 'exact', head: true })
          .eq('parent_id', parentId)
          .then(({ count }) => count ?? 0),
        supabase
          .from('balance_ach_pending_purchases')
          .select('id', { count: 'exact', head: true })
          .eq('parent_id', parentId)
          .then(({ count }) => count ?? 0),
      ]).then(([txCount, zelleCount, achCount]) => {
        setHasBalanceTransactions(txCount > 0 || zelleCount > 0 || achCount > 0);
      });
    }
  }, [parent]);

  // Search for students
  const searchStudents = useCallback(async (searchTerm: string) => {
    if (searchTerm.length < 2) {
      setStudentSearchResults([]);
      return;
    }

    try {
      setSearchingStudents(true);
      const { data, error } = await supabase
        .from('students')
        .select('id, first_name, last_name')
        .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .eq('active', true)
        .limit(10);

      if (error) throw error;

      // Filter out students already associated or about to be added
      const existingStudentIds = new Set([
        ...currentRelationships.filter(r => !relationshipsToRemove.includes(r.student.id)).map(r => r.student.id),
        ...relationshipsToAdd.map(r => r.student_id)
      ]);

      const filteredResults = (data || []).filter(student => !existingStudentIds.has(student.id));
      setStudentSearchResults(filteredResults);
    } catch (error) {
      console.error('Error searching students:', error);
    } finally {
      setSearchingStudents(false);
    }
  }, [currentRelationships, relationshipsToRemove, relationshipsToAdd]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (studentSearch) {
        searchStudents(studentSearch);
      } else {
        setStudentSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [studentSearch, searchStudents]);

  const handleRemoveRelationship = (studentId: string) => {
    setRelationshipsToRemove(prev => [...prev, studentId]);
  };

  const handleUndoRemoveRelationship = (studentId: string) => {
    setRelationshipsToRemove(prev => prev.filter(id => id !== studentId));
  };

  const handleAddStudentRelationship = (student: StudentSearchResult, relationship: string = 'Parent', isPrimary: boolean = false) => {
    setRelationshipsToAdd(prev => [...prev, {
      student_id: student.id,
      student_info: student,
      relationship,
      is_primary: isPrimary
    }]);
    setStudentSearch('');
    setStudentSearchResults([]);
    setShowAddStudent(false);
  };

  const handleRemoveAddedRelationship = (studentId: string) => {
    setRelationshipsToAdd(prev => prev.filter(r => r.student_id !== studentId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Basic validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setFormError('First name and last name are required');
      return;
    }

    if (!formData.email.trim()) {
      setFormError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setFormError('Please enter a valid email address');
      return;
    }

    const postal = parsePostalCode(formData.postal_code);
    if (!postal.ok) {
      setFormError(POSTAL_CODE_ERROR);
      return;
    }

    try {
      await onSave({
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formatPhoneCanonical(formData.phone.trim()),
        address: normalizeAddress(formData.address),
        postal_code: postal.value,
        relationshipsToRemove,
        relationshipsToAdd: relationshipsToAdd.map(rel => ({
          student_id: rel.student_id,
          relationship: rel.relationship,
          is_primary: rel.is_primary
        }))
      });
    } catch (error: unknown) {
      console.error('Error saving parent:', error);
      const errorObj = error as { message?: string };
      setFormError(errorObj.message || 'An unknown error occurred');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, phone: handlePhoneInput(e.target.value) }));
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!parent) return;
    try {
      await onDelete(parent.id);
      setShowDeleteConfirmation(false);
    } catch (error) {
      // Error is handled by the parent component
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  // Check if parent has no students (including relationships to be removed and added)
  const hasNoStudents = parent && 
    (parent.student_parents?.length || 0) - relationshipsToRemove.length + relationshipsToAdd.length === 0;

  if (!parent) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Parent">
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                First Name *
              </label>
              <input
                type="text"
                id="first_name"
                name="first_name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>
            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                id="last_name"
                name="last_name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.phone}
              onChange={handlePhoneChange}
              disabled={isLoading}
              placeholder="(123) 456-7890"
              maxLength={14}
            />
          </div>

          {/* Optional, entered by the parent or here. ZIP 94404 makes this parent's children
              Foster City Residents: a Kaleido program's Foster City Resident discount applies to
              them automatically at enrollment. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <input
                type="text"
                id="address"
                name="address"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.address}
                onChange={handleInputChange}
                disabled={isLoading}
                maxLength={ADDRESS_MAX_LENGTH}
              />
            </div>
            <div>
              <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
                ZIP
              </label>
              <input
                type="text"
                id="postal_code"
                name="postal_code"
                inputMode="numeric"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.postal_code}
                onChange={handleInputChange}
                disabled={isLoading}
                maxLength={POSTAL_CODE_INPUT_MAX_LENGTH}
              />
            </div>
          </div>

          {/* Student Relationships */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Student Relationships</h4>
            
            {/* Current Relationships */}
            <div className="space-y-2 mb-4">
              {currentRelationships.map((rel, index) => {
                const isBeingRemoved = relationshipsToRemove.includes(rel.student.id);
                return (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 border rounded-md ${
                      isBeingRemoved ? 'bg-red-50 border-red-200' : 'bg-gray-50'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {rel.student.first_name} {rel.student.last_name}
                        {rel.is_primary && <span className="ml-2 text-xs text-blue-600">(Primary)</span>}
                      </div>
                      <div className="text-xs text-gray-500">{rel.relationship}</div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {isBeingRemoved ? (
                        <button
                          type="button"
                          onClick={() => handleUndoRemoveRelationship(rel.student.id)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                          disabled={isLoading}
                        >
                          Undo
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRemoveRelationship(rel.student.id)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                          disabled={isLoading}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* New Relationships to Add */}
            {relationshipsToAdd.map((rel, index) => (
              <div key={index} className="flex items-center justify-between p-3 border border-green-200 bg-green-50 rounded-md mb-2">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {rel.student_info.first_name} {rel.student_info.last_name}
                    {rel.is_primary && <span className="ml-2 text-xs text-blue-600">(Primary)</span>}
                  </div>
                  <div className="text-xs text-gray-500">{rel.relationship}</div>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveAddedRelationship(rel.student_id)}
                  className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors"
                  disabled={isLoading}
                >
                  Remove
                </button>
              </div>
            ))}

            {/* Add Student Section */}
            {!showAddStudent ? (
              <button
                type="button"
                onClick={() => setShowAddStudent(true)}
                className="w-full py-2 border-2 border-dashed border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-800 rounded-md transition-colors"
                disabled={isLoading}
              >
                + Add Student Relationship
              </button>
            ) : (
              <div className="border border-gray-300 rounded-md p-3">
                <div className="flex justify-between items-center mb-3">
                  <h5 className="text-sm font-medium text-gray-700">Add Student</h5>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddStudent(false);
                      setStudentSearch('');
                      setStudentSearchResults([]);
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                </div>
                
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search by student name..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={studentSearch}
                    onChange={(e) => setStudentSearch(e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {searchingStudents && (
                  <div className="text-sm text-gray-500 mb-2">Searching...</div>
                )}

                {studentSearchResults.length > 0 && (
                  <div className="space-y-1">
                    {studentSearchResults.map((student) => (
                      <div
                        key={student.id}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 rounded"
                      >
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {student.first_name} {student.last_name}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleAddStudentRelationship(student)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                          disabled={isLoading}
                        >
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {studentSearch.length >= 2 && !searchingStudents && studentSearchResults.length === 0 && (
                  <div className="text-sm text-gray-500">No students found.</div>
                )}
              </div>
            )}
          </div>

          {formError && (
            <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
              {formError}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            {hasNoStudents && !hasBalanceTransactions && (
              <button
                type="button"
                onClick={handleDeleteClick}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isLoading || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isLoading || isDeleting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isLoading || isDeleting}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-end sm:items-center sm:justify-center z-[60]"
             onClick={e => { if (e.target === e.currentTarget) handleCancelDelete(); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 sm:max-w-md w-full sm:mx-4">
            <div className="sm:hidden flex justify-center -mt-2 mb-4">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Delete Parent</h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete <strong>{parent?.first_name} {parent?.last_name}</strong>? 
                This action cannot be undone and will permanently remove this parent from the system.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}

interface ParentStatusReport {
  parent_email: string;
  parent_name: string;
  number_of_students: number;
  status: string;
  enrolled_students: number;
  students_with_payments: number;
  students_without_enrollment: string;
  last_login: string | null;
}

interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: ParentStatusReport[];
  onDownload: () => void;
  isLoading: boolean;
}

function PreviewModal({ isOpen, onClose, data, onDownload, isLoading }: PreviewModalProps) {
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Parent Status Report Preview">
      <div className="p-6">
        <div className="mb-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Preview Report</h3>
          <p className="text-sm text-gray-600">
            Showing {data.length} parents. Review the data below before downloading the CSV.
          </p>
        </div>

        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parent
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Students
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{row.parent_name}</div>
                      <div className="text-xs text-gray-500">{row.parent_email}</div>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                    {row.number_of_students}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      row.status === 'all students paid' ? 'bg-green-100 text-green-800' :
                      row.status === 'partial students paid' ? 'bg-yellow-100 text-yellow-800' :
                      row.status === 'no payment but enrolled (at least one)' ? 'bg-blue-100 text-blue-800' :
                      row.status === 'no enrollment yet' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {row.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={isLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

interface AddParentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (parentData: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
  }) => Promise<void>;
  isLoading: boolean;
}

function AddParentModal({ isOpen, onClose, onSave, isLoading }: AddParentModalProps) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });
  const [formError, setFormError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, phone: handlePhoneInput(e.target.value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    // Basic validation
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setFormError('First name and last name are required');
      return;
    }

    if (!formData.email.trim()) {
      setFormError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setFormError('Please enter a valid email address');
      return;
    }

    try {
      await onSave({
        ...formData,
        phone: formatPhoneCanonical(formData.phone.trim()),
      });
      // Reset form on success
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: ''
      });
      setFormError(null);
    } catch (error) {
      const errorObj = error as { message?: string };
      setFormError(errorObj.message || 'An error occurred while creating the parent');
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New Parent">
      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
              First Name *
            </label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.first_name}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            />
          </div>

          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name *
            </label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.last_name}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
              required
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
              Phone (Optional)
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.phone}
              onChange={handlePhoneChange}
              disabled={isLoading}
              placeholder="(123) 456-7890"
              maxLength={14}
            />
          </div>

          {formError && (
            <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
              {formError}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Parent'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

// Helper function to copy parent ID to clipboard
const copyParentId = (parentId: string) => {
  navigator.clipboard.writeText(parentId);
  alert('Parent ID copied to clipboard!');
};

export function AdminParents() {
  const [parents, setParents] = useState<Parent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [sortField, setSortField] = useState<'name' | 'created_at'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [editParentModal, setEditParentModal] = useState<{
    isOpen: boolean;
    parent: Parent | null;
  }>({
    isOpen: false,
    parent: null,
  });
  const [editLoading, setEditLoading] = useState(false);
  const [csvLoading, setCsvLoading] = useState(false);
  const [previewData] = useState<ParentStatusReport[]>([]);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [addParentModal, setAddParentModal] = useState<{
    isOpen: boolean;
    isLoading: boolean;
  }>({
    isOpen: false,
    isLoading: false,
  });
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  // `?parent=<id>` (from search, or a pasted link) narrows the directory to that one parent.
  const [focusedParentId, clearFocusedParent] = useFocusedRecordId(PARENT_PARAM);

  // Pagination (100 per page)
  const PAGE_SIZE = 100;
  const [parentsPage, setParentsPage] = useState<number>(1);
  const [parentsTotal, setParentsTotal] = useState<number>(0);

  // Refetch when page, sort, search or the linked parent changes
  useEffect(() => {
    fetchParents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentsPage, sortField, sortDirection, appliedSearchTerm, focusedParentId]);

  // Arriving on a link opens that parent's row, and starts the pager over: a link followed
  // from page 3 of the full list should not read "201–1 of 1".
  useEffect(() => {
    if (!focusedParentId) return;
    setSelectedParentId(focusedParentId);
    setParentsPage(1);
  }, [focusedParentId]);

  // Searching means "show me the list again", so it lets go of a linked parent.
  const applySearch = () => {
    if (focusedParentId) clearFocusedParent();
    setParentsPage(1);
    setAppliedSearchTerm(searchTerm.trim());
  };

  // Sort function
  const handleSort = (field: 'name' | 'created_at') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setParentsPage(1);
  };

  // Sort icon component
  const SortIcon = ({ field }: { field: 'name' | 'created_at' }) => {
    if (sortField !== field) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortDirection === 'asc' ? 
      <span className="text-blue-600">↑</span> : 
      <span className="text-blue-600">↓</span>;
  };

  async function fetchParents() {
    try {
      setLoading(true);
      const offset = (parentsPage - 1) * PAGE_SIZE;
      const baseParentSelect = `
        *,
        student_parents (
          relationship,
          is_primary,
          student:students (
            id,
            first_name,
            last_name
          )
        )
      `;

      const runDefaultQuery = async () => {
        let query = supabase
          .from('parents')
          .select(baseParentSelect, { count: 'exact' });

        if (sortField === 'created_at') {
          query = query.order('created_at', { ascending: sortDirection === 'asc' });
        } else {
          query = query
            .order('last_name', { ascending: sortDirection === 'asc' })
            .order('first_name', { ascending: sortDirection === 'asc' });
        }

        const { data, error, count } = await query.range(offset, offset + PAGE_SIZE - 1);
        if (error) throw error;
        setParentsTotal(typeof count === 'number' ? count : 0);
        return data ?? [];
      };

      const runSearchQuery = async () => {
        const keywords = appliedSearchTerm.toLowerCase().trim().split(/\s+/).filter(Boolean);
        // Ask server for matching ids for this page
        const { data: idRows, error: rpcError } = await supabase.rpc('search_parents', {
          p_keywords: keywords,
          p_limit: PAGE_SIZE,
          p_offset: offset,
          p_sort_field: sortField,
          p_sort_direction: sortDirection,
        });
        if (rpcError) throw rpcError;
        const ids: string[] = (idRows || []).map((r: { parent_id: string }) => r.parent_id);
        const totalCount = (idRows && idRows[0] && typeof (idRows[0] as { total_count?: number }).total_count === 'number')
          ? (idRows[0] as { total_count: number }).total_count
          : 0;
        setParentsTotal(totalCount);
        if (ids.length === 0) return [] as Parent[];

        // Fetch details for those ids and preserve order returned by RPC
        const { data: parentsData, error: parentsErr } = await supabase
          .from('parents')
          .select(baseParentSelect)
          .in('id', ids);
        if (parentsErr) throw parentsErr;

        const orderIndex = new Map<string, number>();
        ids.forEach((id, idx) => orderIndex.set(id, idx));
        const ordered = [...(parentsData ?? [])].sort((a, b) => {
          const ia = orderIndex.get(a.id) ?? 0;
          const ib = orderIndex.get(b.id) ?? 0;
          return ia - ib;
        });
        return ordered as Parent[];
      };

      // A link names one parent; the list is that parent alone, whatever the search box says.
      const runFocusedQuery = async () => {
        const { data, error } = await supabase
          .from('parents')
          .select(baseParentSelect)
          .eq('id', focusedParentId as string);
        if (error) throw error;
        setParentsTotal(data?.length ?? 0);
        return (data ?? []) as Parent[];
      };

      const parentsData: Parent[] = focusedParentId
        ? await runFocusedQuery()
        : appliedSearchTerm.trim() ? await runSearchQuery() : await runDefaultQuery();

      // Get all parent emails and ids for follow-up queries
      const parentEmails = parentsData.map(p => p.email).filter((e): e is string => Boolean(e));
      const parentIds = parentsData.map(p => p.id);

      // Fetch the latest event for each parent via RPC (much fewer rows)
      const { data: eventsData, error: eventsError } = await supabase
        .rpc('get_latest_events_by_email', { p_emails: parentEmails });
      if (eventsError) {
        console.error('Error fetching app events:', eventsError);
      }

      // Map latest events by email
      const latestEventsMap = new Map<string, { event_type: string; created_at: string; user_agent: string | null; metadata: Record<string, unknown> }>();
      if (eventsData) {
        for (const event of eventsData as Array<{ email: string; event_type: string; created_at: string; user_agent: string | null; metadata: Record<string, unknown> }>) {
          if (event.email && !latestEventsMap.has(event.email)) {
            latestEventsMap.set(event.email, {
              event_type: event.event_type,
              created_at: event.created_at,
              user_agent: event.user_agent,
              metadata: event.metadata,
            });
          }
        }
      }

      // Attach events
      const parentsWithEvents = parentsData.map(parent => ({
        ...parent,
        latest_event: parent.email ? latestEventsMap.get(parent.email) : undefined,
      }));

      // Fetch payment preferences for all parents
      let parentsWithPrefs = parentsWithEvents as Parent[];
      try {
        type PrefRow = {
          parent_id: string;
          default_payment_method: 'card' | 'bank_account' | 'zelle' | null;
          autopay_enabled: boolean;
          default_bank_account_setup_intent_id: string | null;
        };
        const { data: prefsData, error: prefsError } = await supabase
          .from('parent_payment_preferences')
          .select('parent_id, default_payment_method, autopay_enabled, default_bank_account_setup_intent_id')
          .in('parent_id', parentIds);

        if (prefsError) throw prefsError;

        const prefsMap = new Map<string, PrefRow>();
        (prefsData || []).forEach((row: PrefRow) => {
          prefsMap.set(row.parent_id, row);
        });

        // Fetch all active cards for every parent in this page
        type CardRow = { parent_id: string; brand: string | null; last4: string | null };
        const cardsMap = new Map<string, CardRow[]>();
        {
          const { data: cardData } = await supabase
            .from('card_payment_methods')
            .select('parent_id, brand, last4')
            .in('parent_id', parentIds)
            .eq('is_active', true)
            .order('created_at', { ascending: false });
          (cardData || []).forEach((row: CardRow) => {
            const arr = cardsMap.get(row.parent_id) ?? [];
            arr.push(row);
            cardsMap.set(row.parent_id, arr);
          });
        }

        // Fetch all active succeeded bank accounts for every parent in this page
        type BankRow = { id: string; parent_id: string; bank_name: string | null; last4: string | null; account_type: string | null };
        const banksMap = new Map<string, BankRow[]>();
        {
          const { data: bankData } = await supabase
            .from('bank_account_setup_intents')
            .select('id, parent_id, bank_name, last4, account_type')
            .in('parent_id', parentIds)
            .eq('is_active', true)
            .eq('status', 'succeeded')
            .order('created_at', { ascending: false });
          (bankData || []).forEach((row: BankRow) => {
            const arr = banksMap.get(row.parent_id) ?? [];
            arr.push(row);
            banksMap.set(row.parent_id, arr);
          });
        }

        parentsWithPrefs = parentsWithEvents.map(p => {
          const pref = prefsMap.get(p.id);
          const rawCards = cardsMap.get(p.id) ?? [];
          const rawBanks = banksMap.get(p.id) ?? [];
          const defaultBankIntentId = pref?.default_bank_account_setup_intent_id ?? null;

          // The default card is the most recent active one (index 0 after desc sort)
          const cards_on_file: CardOnFile[] = rawCards.map((c, i) => ({
            brand: c.brand,
            last4: c.last4,
            is_default: pref?.default_payment_method === 'card' && i === 0,
          }));

          // The default bank is the pinned intent, or the most recent if not pinned
          const banks_on_file: BankOnFile[] = rawBanks.map((b, i) => ({
            bank_name: b.bank_name,
            last4: b.last4,
            account_type: b.account_type,
            is_default:
              pref?.default_payment_method === 'bank_account' &&
              (defaultBankIntentId ? b.id === defaultBankIntentId : i === 0),
          }));

          return {
            ...p,
            default_payment_method: pref?.default_payment_method ?? null,
            autopay_enabled: pref?.autopay_enabled ?? false,
            cards_on_file,
            banks_on_file,
          } as Parent;
        });
      } catch (prefsErr) {
        console.error('Error fetching parent payment preferences:', prefsErr);
        // proceed without prefs
      }

      setParents(parentsWithPrefs || []);
      setLoading(false);
    } catch (error: unknown) {
      console.error('Error fetching parents:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred');
      setLoading(false);
    }
  }

  async function handleEditParent(parentData: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    address: string | null;
    postal_code: string | null;
    relationshipsToRemove: string[];
    relationshipsToAdd: Array<{
      student_id: string;
      relationship: string;
      is_primary: boolean;
    }>;
  }) {
    if (!editParentModal.parent) return;

    try {
      setEditLoading(true);
      const before = editParentModal.parent;

      // Address and ZIP are sent only when they changed, unlike the fields above, which are
      // always sent. Saving a parent whose address was never touched then works on a database
      // that does not have the columns yet.
      const addressUpdate = changedAddressFields(before, parentData);

      // Update parent basic info
      const { error: parentError } = await supabase
        .from('parents')
        .update({
          first_name: parentData.first_name,
          last_name: parentData.last_name,
          email: parentData.email,
          phone: parentData.phone || null,
          ...addressUpdate,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editParentModal.parent.id);

      if (parentError) throw parentError;

      // Field names only, never the values — the values are exactly the PII this table
      // should not accumulate. Mirrors the parent's own `parent_profile_update`.
      const changedFields = [
        ...(['first_name', 'last_name', 'email', 'phone'] as const)
          .filter(field => (parentData[field] || null) !== (before[field] || null)),
        ...Object.keys(addressUpdate),
      ];

      analytics.trackAdminProfileUpdate({
        parentId: before.id,
        parentEmail: parentData.email,
        parentName: `${parentData.first_name} ${parentData.last_name}`.trim(),
        changedFields,
      });

      // Remove relationships
      if (parentData.relationshipsToRemove.length > 0) {
        const { error: removeError } = await supabase
          .from('student_parents')
          .delete()
          .eq('parent_id', editParentModal.parent.id)
          .in('student_id', parentData.relationshipsToRemove);

        if (removeError) throw removeError;

        analytics.trackAdminAction('student_parent_unlink', {
          entityType: 'parent',
          entityId: editParentModal.parent.id,
          metadata: {
            parent_email: parentData.email,
            student_ids: parentData.relationshipsToRemove,
            count: parentData.relationshipsToRemove.length,
          },
        });
      }

      // Add new relationships
      if (parentData.relationshipsToAdd.length > 0) {
        const relationshipPromises = parentData.relationshipsToAdd.map(rel => {
          return supabase.rpc('admin_add_student_parent_relationship', {
            p_student_id: rel.student_id,
            p_parent_id: editParentModal.parent!.id,
            p_relationship: rel.relationship,
            p_is_primary: rel.is_primary,
            p_can_pay: true // Defaulting to true for now
          });
        });

        const results = await Promise.all(relationshipPromises);
        results.forEach(result => {
          if (result.error) throw result.error;
        });

        analytics.trackAdminAction('student_parent_link', {
          entityType: 'parent',
          entityId: editParentModal.parent.id,
          metadata: {
            parent_email: parentData.email,
            student_ids: parentData.relationshipsToAdd.map(rel => rel.student_id),
            count: parentData.relationshipsToAdd.length,
          },
        });
      }

      // Close modal and refresh data
      setEditParentModal({ isOpen: false, parent: null });
      setSuccessMessage('Parent updated successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      fetchParents();
    } catch (error: unknown) {
      console.error('Error updating parent:', error);
      throw error; // Re-throw to let the modal handle the error display
    } finally {
      setEditLoading(false);
    }
  }

  async function handleDeleteParent(parentId: string) {
    try {
      setDeleteLoading(true);
      
      // Delete the parent
      const { error } = await supabase
        .from('parents')
        .delete()
        .eq('id', parentId);

      if (error) throw error;

      // Close modal and refresh data
      setEditParentModal({ isOpen: false, parent: null });
      setSuccessMessage('Parent deleted successfully!');
      setTimeout(() => setSuccessMessage(null), 5000);
      fetchParents();
    } catch (error: unknown) {
      console.error('Error deleting parent:', error);
      const errorObj = error as { message?: string };
      setError(errorObj.message || 'An unknown error occurred while deleting the parent');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleDownloadCSV() {
    setCsvLoading(true);
    const headers = ['parent email', 'parent name', 'number of students', 'status', 'enrolled students', 'students with payments', 'students without enrollment', 'last login'];
    const csvContent = [
      headers.join(','),
      ...previewData.map(row => [
        `"${row.parent_email}"`,
        `"${row.parent_name}"`,
        row.number_of_students,
        `"${row.status}"`,
        row.enrolled_students,
        row.students_with_payments,
        `"${row.students_without_enrollment || 'N/A'}"`,
        `"${row.last_login ? formatDateInBusinessTZ(row.last_login) : 'N/A'}"`
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `parent_status_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setCsvLoading(false);
    setIsPreviewModalOpen(false);
    setSuccessMessage('Report downloaded successfully.');
    setTimeout(() => setSuccessMessage(null), 5000);
  }

  // All filtering and sorting are performed on the server now.

  if (error) {
    return (
      <AdminLayout>
        <div className="mb-4 p-4 bg-red-50 text-red-600 rounded">
          {error}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="bg-white shadow overflow-hidden sm:rounded-lg lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <div className="relative flex-1 sm:flex-none sm:w-80">
              <input
                type="text"
                placeholder="Search by name, email, phone, or student names..."
                className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') applySearch();
                }}
              />
            </div>
            <button
              onClick={() => setAddParentModal({ isOpen: true, isLoading: false })}
              className="sm:hidden w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-xl leading-none hover:bg-blue-700 transition-colors flex-shrink-0"
              aria-label="Add parent"
            >
              +
            </button>
            <button
              onClick={() => setAddParentModal({ isOpen: true, isLoading: false })}
              className="hidden sm:block px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium whitespace-nowrap flex-shrink-0"
            >
              Add Parent
            </button>
          </div>
        </div>

        {focusedParentId && (
          <FocusedRecordBanner
            noun="parent"
            loading={loading}
            found={parents.length > 0}
            onShowAll={clearFocusedParent}
          />
        )}

        {successMessage && (
          <div className="m-4 p-4 bg-green-50 text-green-700 rounded-md">
            {successMessage}
          </div>
        )}

        {/* Top Pagination */}
        <div className="p-4 pb-2 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
          {(() => {
            const totalPages = Math.max(1, Math.ceil(parentsTotal / PAGE_SIZE));
            const start = (parentsPage - 1) * PAGE_SIZE + 1;
            const end = Math.min(parentsPage * PAGE_SIZE, parentsTotal);
            return (
              <>
                <div className="text-sm text-gray-600 flex items-center gap-1.5">
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
                      Loading parents...
                    </>
                  ) : (
                    `Showing ${parentsTotal === 0 ? 0 : start} to ${end} of ${parentsTotal} parents`
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setParentsPage(1)}
                    disabled={parentsPage === 1}
                  >
                    First
                  </button>
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setParentsPage(Math.max(1, parentsPage - 1))}
                    disabled={parentsPage === 1}
                  >
                    Prev
                  </button>
                  <span className="text-sm text-gray-700">
                    Page {parentsPage} of {totalPages}
                  </span>
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setParentsPage(Math.min(totalPages, parentsPage + 1))}
                    disabled={parentsPage === totalPages}
                  >
                    Next
                  </button>
                  <button
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
                    onClick={() => setParentsPage(totalPages)}
                    disabled={parentsPage === totalPages}
                  >
                    Last
                  </button>
                </div>
              </>
            );
          })()}
        </div>

        <div className="overflow-x-auto overflow-y-auto lg:flex-1 lg:min-h-0">
          <table className="min-w-full divide-y divide-gray-200 hidden lg:table">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center space-x-1">
                    <span>NAME</span>
                    <SortIcon field="name" />
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  CONTACT
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  STUDENTS
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  LATEST ACTIVITY
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">
                  STATUS
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 bg-gray-50"
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center space-x-1">
                    <span>JOINED</span>
                    <SortIcon field="created_at" />
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">PAYMENT METHODS</th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">AUTO PAY</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parents.map((parent) => (
                <React.Fragment key={parent.id}>
                  <tr
                    className={selectedParentId === parent.id ? 'bg-blue-50 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'}
                    onClick={() => setSelectedParentId(selectedParentId === parent.id ? null : parent.id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {parent.first_name} {parent.last_name}
                      </div>
                      {isFosterCityZip(parent.postal_code) && <FosterCityResidentBadge className="mt-1" />}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{parent.email}</div>
                      {parent.phone && <div className="text-sm text-gray-500">{parent.phone}</div>}
                      {parent.postal_code && <div className="text-sm text-gray-500">ZIP {parent.postal_code}</div>}
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        {parent.student_parents?.map((sp, index) => (
                          <div key={index} className="text-sm">
                            <div className="font-medium text-gray-900">
                              {sp.student.first_name} {sp.student.last_name}
                              {sp.is_primary && <span className="ml-2 text-xs text-blue-600">(Primary)</span>}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {sp.relationship}
                            </div>
                          </div>
                        ))}
                        {(!parent.student_parents || parent.student_parents.length === 0) && (
                          <div className="text-sm text-gray-500">No students</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {parent.latest_event ? (
                        <div className="flex items-center space-x-2">
                          <DeviceIcon deviceType={getDeviceType(parent.latest_event)} />
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {parent.latest_event.event_type.replace(/_/g, ' ')}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDateInBusinessTZ(parent.latest_event.created_at)}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-500">No activity</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        parent.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {parent.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDateInBusinessTZ(parent.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col gap-1.5">
                        {parent.cards_on_file?.map((card, i) => (
                          <CardPaymentBadge
                            key={i}
                            brand={card.brand}
                            last4={card.last4}
                            isDefault={card.is_default}
                          />
                        ))}
                        {parent.banks_on_file?.map((bank, i) => (
                          <BankPaymentBadge
                            key={i}
                            bank_name={bank.bank_name}
                            last4={bank.last4}
                            isDefault={bank.is_default}
                          />
                        ))}
                        {!parent.cards_on_file?.length && !parent.banks_on_file?.length && (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        parent.autopay_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {parent.autopay_enabled ? 'On' : 'Off'}
                      </span>
                    </td>
                  </tr>
                  {selectedParentId === parent.id && (
                    <tr key={`${parent.id}-actions`} className="bg-blue-50">
                      <td colSpan={8} className="px-6 py-3 border-t border-blue-100">
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); copyParentId(parent.id); }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                            title="Copy parent ID to clipboard"
                          >
                            <Copy className="h-3 w-3 mr-1" />
                            Copy ID
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); setEditParentModal({ isOpen: true, parent }); }}
                            className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                          >
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </button>
                        </div>
                        <div className="mt-3" onClick={(e) => e.stopPropagation()}>
                          <ParentAccountDetails parent={parent} />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          {/* Mobile Card View */}
          <div className="lg:hidden">
            {parents.map((parent) => (
              <div
                key={parent.id}
                className={`border-b border-gray-200 p-3 cursor-pointer${selectedParentId === parent.id ? ' bg-blue-50' : ''}`}
                onClick={() => setSelectedParentId(selectedParentId === parent.id ? null : parent.id)}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="text-base font-bold text-gray-900 flex items-center">
                      <span>{parent.first_name} {parent.last_name}</span>
                      <span className={`ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        parent.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {parent.active ? 'Active' : 'Inactive'}
                      </span>
                      {isFosterCityZip(parent.postal_code) && <FosterCityResidentBadge className="ml-2" />}
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      <span>{parent.email}</span>
                      <span className="mx-1.5">|</span>
                      <span>{parent.phone || 'No Phone'}</span>
                      {parent.postal_code && (
                        <>
                          <span className="mx-1.5">|</span>
                          <span>ZIP {parent.postal_code}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="text-sm space-y-1">
                  <div>
                    <span className="font-medium text-gray-500">Students: </span>
                    {parent.student_parents?.length > 0 ? parent.student_parents.map((sp, index) => (
                      <span key={index} className="text-gray-800">
                        {sp.student.first_name} {sp.student.last_name}{sp.is_primary ? ' (P)' : ''}{index < parent.student_parents.length - 1 ? ', ' : ''}
                      </span>
                    )) : <span className="text-gray-500">None</span>}
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Payment Methods: </span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {parent.cards_on_file?.map((card, i) => (
                        <CardPaymentBadge
                          key={i}
                          brand={card.brand}
                          last4={card.last4}
                          isDefault={card.is_default}
                        />
                      ))}
                      {parent.banks_on_file?.map((bank, i) => (
                        <BankPaymentBadge
                          key={i}
                          bank_name={bank.bank_name}
                          last4={bank.last4}
                          isDefault={bank.is_default}
                        />
                      ))}
                      {!parent.cards_on_file?.length && !parent.banks_on_file?.length && (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Auto Pay: </span>
                    <span className="text-gray-800">{parent.autopay_enabled ? 'On' : 'Off'}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-500">Latest Activity: </span>
                    {parent.latest_event ? (
                      <span className="text-gray-800 flex items-center space-x-1">
                        <DeviceIcon deviceType={getDeviceType(parent.latest_event)} />
                        <span>
                          {parent.latest_event.event_type.replace(/_/g, ' ')} on <span className="text-gray-600">{formatDateInBusinessTZ(parent.latest_event.created_at)}</span>
                        </span>
                      </span>
                    ) : (
                      <span className="text-gray-500">None</span>
                    )}
                  </div>
                </div>
                {selectedParentId === parent.id && (
                  <div className="mt-2 pt-2 border-t border-blue-100 flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => copyParentId(parent.id)}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 hover:text-gray-900 transition-colors"
                      title="Copy parent ID to clipboard"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Copy ID
                    </button>
                    <button
                      onClick={() => setEditParentModal({ isOpen: true, parent })}
                      className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </button>
                  </div>
                )}
                {selectedParentId === parent.id && (
                  <div className="mt-2 pt-2 border-t border-blue-100" onClick={(e) => e.stopPropagation()}>
                    <ParentAccountDetails parent={parent} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {!loading && parents.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm ? 'No parents found matching your search.' : 'No parents found.'}
              </p>
            </div>
          )}
        </div>

      </div>

      <EditParentModal
        isOpen={editParentModal.isOpen}
        onClose={() => setEditParentModal({ isOpen: false, parent: null })}
        parent={editParentModal.parent}
        onSave={handleEditParent}
        onDelete={handleDeleteParent}
        isLoading={editLoading}
        isDeleting={deleteLoading}
      />
      <PreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        data={previewData}
        onDownload={handleDownloadCSV}
        isLoading={csvLoading}
      />
      <AddParentModal
        isOpen={addParentModal.isOpen}
        onClose={() => setAddParentModal({ isOpen: false, isLoading: false })}
        onSave={async (data) => {
          try {
            const { normalizeEmail } = await import('../../utils/string');
            setAddParentModal({ ...addParentModal, isLoading: true });
            const { error } = await supabase
              .from('parents')
              .insert({
                first_name: data.first_name,
                last_name: data.last_name,
                email: normalizeEmail(data.email),
                phone: data.phone || null,
                active: true, // New parents are active by default
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              });

            if (error) throw error;

            setSuccessMessage('Parent created successfully!');
            setTimeout(() => setSuccessMessage(null), 5000);
            fetchParents();
            setAddParentModal({ isOpen: false, isLoading: false });
          } catch (err) {
            const errorObj = err as { message?: string };
            setError(errorObj.message || 'An error occurred while creating the parent');
            setAddParentModal({ ...addParentModal, isLoading: false });
          }
        }}
        isLoading={addParentModal.isLoading}
      />
    </AdminLayout>
  );
} 